import { Component, ViewChild, ElementRef, OnInit, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Utils } from '../../shared/utils';
import { Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { LanguageSelectorComponent } from '../designs/language-selector/language-selector.component';
import { clearSessionAuthToken, getStoredAuthToken } from '../../core/auth/auth-token';
import { LanguageService } from '../../core/services/language/language.service';
import { filter, Subscription } from 'rxjs';

@Component({
  selector: 'app-mobile-navbar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    LanguageSelectorComponent
  ],
  templateUrl: './mobile-navbar.html',
  styleUrl: './mobile-navbar.scss'
})
export class MobileNavbar implements OnInit, OnDestroy {
  @ViewChild('navbarCollapse') navbarCollapse!: ElementRef<HTMLDivElement>;
  @ViewChild('profileImage') profileImg!: ElementRef<HTMLImageElement>;
  public readonly languageService = inject(LanguageService);

  isMenuOpen = false;
  isCalendarDrawerOpen = false;
  showProfileDropdown = false;
  authToken: any = '';
  backendUrl: string = environment.backendUrl;
  frontendUrl: string = environment.frontendUrl;
  userDetails: any = null;
  expandedSection: 'movie' | 'tv' | 'person' | null = null;

  toggleSection(section: 'movie' | 'tv' | 'person'): void {
    this.expandedSection = this.expandedSection === section ? null : section;
  }

  private readonly platformId = inject(PLATFORM_ID);
  private router = inject(Router);
  private routerSub?: Subscription;

  constructor(
    private cookieService: CookieService,
    private http: HttpClient,
    private utils: Utils
  ) {}

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.authToken = getStoredAuthToken();
    this.getUserDetails();

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.closeMenu();
      this.showProfileDropdown = false;
    });
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
    this.updateBodyScrollLock(false);
  }

  getUserDetails(): void {
    const url = this.authToken && this.authToken !== 'undefined' && this.authToken !== 'null'
      ? `${this.backendUrl}/auth/session?session_token=${this.authToken}`
      : `${this.backendUrl}/auth/session`;

    this.http.get(url).subscribe({
      next: (res) => {
        this.userDetails = res;
        if (this.userDetails?.profile_picture) {
          this.userDetails.profile_picture = this.userDetails.profile_picture.replace('=s96-c', '');
        }
      },
      error: (error: HttpErrorResponse) => {
        this.userDetails = null;
        this.handleSessionError(error);
      }
    });
  }

  private handleSessionError(error: HttpErrorResponse): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    clearSessionAuthToken();
    this.authToken = '';
  }

  toggleCollapse(): void {
    this.isMenuOpen = !this.isMenuOpen;
    if (this.isMenuOpen) {
      this.showProfileDropdown = false;
      this.updateBodyScrollLock(true);
    } else {
      this.updateBodyScrollLock(false);
    }
  }

  closeMenu(): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
      this.updateBodyScrollLock(false);
    }
  }

  toggleCalendarDrawer(): void {
    this.isCalendarDrawerOpen = !this.isCalendarDrawerOpen;
    if (this.isCalendarDrawerOpen) {
      this.closeMenu();
    }
  }

  openCalendarDrawer(): void {
    this.isCalendarDrawerOpen = true;
    this.closeMenu();
  }

  closeCalendarDrawer(): void {
    this.isCalendarDrawerOpen = false;
  }

  private updateBodyScrollLock(lock: boolean): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (lock) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }

  onImageError(event: Event): void {
    const target = event.target as HTMLImageElement;
    target.src = '/assets/images/avatar.png';
  }

  profileNameFilter(name: string): string {
    return (name && this.utils.userProfileNameFilter(name)) || name || 'user';
  }

  logout(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.showProfileDropdown = false;

    sessionStorage.setItem('pending_toast', JSON.stringify({ type: 'info', title: 'See you later!', message: 'You have been signed out. Come back soon!' }));

    const logoutUrl = this.authToken
      ? `${this.backendUrl}/auth/session/logout?session_token=${this.authToken}`
      : `${this.backendUrl}/auth/session/logout`;

    this.http.get(logoutUrl).subscribe({
      next: () => {
        clearSessionAuthToken();
        location.assign('/');
      },
      error: () => {
        clearSessionAuthToken();
        location.assign('/');
      }
    });
  }
}
